/**
 * Recursive JSON editor used by the JD Normalizer Review UI.
 * Splits the recursive component out into its own module so CRA's babel transformer
 * doesn't recurse into the parent file's JSX tree.
 */
import React, { useState } from "react";
import { Layers, ChevronRight } from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

export function BlockSection({ title, block, value, original, onChange, readOnly, showAttribution, provenance }) {
    const [open, setOpen] = useState(true);
    const modifiedPaths = new Map();
    (provenance.fields_modified_in_review || []).forEach(m => {
        if (m.field_path && m.field_path.startsWith(block)) modifiedPaths.set(m.field_path, m);
    });

    return (
        <div className="border border-border/40 bg-background/30">
            <button onClick={() => setOpen(o => !o)}
                data-testid={`block-${block}`}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-white/[0.02]">
                <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-gray-600" />
                    <span className="text-sm font-display text-white capitalize">{title}</span>
                    {modifiedPaths.size > 0 && (
                        <span className="text-[10px] font-mono text-blue-300 px-1.5 py-0.5 border border-blue-500/40 bg-blue-500/10">
                            {modifiedPaths.size} edited
                        </span>
                    )}
                </div>
                <ChevronRight className={cls("w-4 h-4 text-gray-600 transition-transform", open && "rotate-90")} />
            </button>
            {open && (
                <div className="border-t border-border/40 p-3 space-y-3">
                    <JsonEditor
                        path={block}
                        value={value}
                        original={original}
                        onChange={onChange}
                        readOnly={readOnly}
                        showAttribution={showAttribution}
                        modifiedPaths={modifiedPaths}
                    />
                </div>
            )}
        </div>
    );
}

export function JsonEditor(props) {
    const { path, value, original, onChange, readOnly, showAttribution, modifiedPaths, depth = 0 } = props;

    if (Array.isArray(value)) return <ArrayEditor {...props} />;
    if (value && typeof value === "object") return <ObjectEditor {...props} />;
    return <PrimitiveEditor value={value} onChange={onChange} readOnly={readOnly} />;
}

function ArrayEditor({ path, value, original, onChange, readOnly, showAttribution, modifiedPaths, depth = 0 }) {
    const removeIndex = (i) => {
        const next = [...value];
        next.splice(i, 1);
        onChange(next);
    };
    const addItem = () => {
        const sample = (value && value.length) ? value[0] : "";
        const newItem = sample && typeof sample === "object" && !Array.isArray(sample)
            ? Object.fromEntries(Object.keys(sample).map(k => [k, ""]))
            : "";
        onChange([...(value || []), newItem]);
    };
    const setIndex = (i, v) => {
        const next = [...value];
        next[i] = v;
        onChange(next);
    };
    return (
        <div className="space-y-2">
            {value.map((item, i) => (
                <div key={i} className="border border-border/30 p-2 relative" data-testid={`array-item-${path}-${i}`}>
                    {!readOnly && (
                        <button onClick={() => removeIndex(i)}
                            data-testid={`remove-item-${path}-${i}`}
                            className="absolute top-1 right-1 text-[10px] text-gray-600 hover:text-red-400">×</button>
                    )}
                    <JsonEditor
                        path={`${path}[${i}]`}
                        value={item}
                        original={original ? original[i] : undefined}
                        onChange={(v) => setIndex(i, v)}
                        readOnly={readOnly}
                        showAttribution={showAttribution}
                        modifiedPaths={modifiedPaths}
                        depth={depth + 1}
                    />
                </div>
            ))}
            {!readOnly && (
                <button onClick={addItem}
                    data-testid={`add-item-${path}`}
                    className="text-[11px] text-gray-500 hover:text-white font-mono">
                    + Add item
                </button>
            )}
        </div>
    );
}

function ObjectEditor({ path, value, original, onChange, readOnly, showAttribution, modifiedPaths, depth = 0 }) {
    const setKey = (k, v) => onChange({ ...(value || {}), [k]: v });
    const entries = Object.entries(value).filter(([k]) => k !== "_inferred");
    return (
        <div className={cls("space-y-2", depth > 0 && "pl-3 border-l border-border/30")}>
            {entries.map(([k, v]) => {
                const subPath = `${path}.${k}`;
                const wasModified = modifiedPaths && modifiedPaths.has(subPath);
                const inferred = value._inferred === true;
                return (
                    <ObjectField
                        key={k}
                        fieldKey={k}
                        subPath={subPath}
                        v={v}
                        original={original ? original[k] : undefined}
                        wasModified={wasModified}
                        inferred={inferred}
                        showAttribution={showAttribution}
                        readOnly={readOnly}
                        modifiedPaths={modifiedPaths}
                        onChange={(nv) => setKey(k, nv)}
                        depth={depth + 1}
                    />
                );
            })}
        </div>
    );
}

function ObjectField({ fieldKey, subPath, v, original, wasModified, inferred, showAttribution, readOnly, modifiedPaths, onChange, depth }) {
    const aiValue = wasModified && modifiedPaths ? modifiedPaths.get(subPath)?.ai_value : null;
    return (
        <div className="grid grid-cols-12 gap-2 items-start">
            <label className={cls("col-span-3 text-xs pt-1.5 font-mono", wasModified ? "text-blue-300" : "text-gray-500")}>
                {fieldKey}
                {inferred && showAttribution && <span className="text-amber-300 ml-1" title="AI-inferred from source">●</span>}
                {wasModified && <span className="ml-1 text-blue-300" title="Human-edited">✎</span>}
            </label>
            <div className="col-span-9">
                <JsonEditor
                    path={subPath}
                    value={v}
                    original={original}
                    onChange={onChange}
                    readOnly={readOnly}
                    showAttribution={showAttribution}
                    modifiedPaths={modifiedPaths}
                    depth={depth}
                />
                {showAttribution && wasModified && (
                    <p className="text-[10px] font-mono text-gray-600 mt-1">
                        AI: <span className="text-gray-400">{JSON.stringify(aiValue)}</span>
                    </p>
                )}
            </div>
        </div>
    );
}

function PrimitiveEditor({ value, onChange, readOnly }) {
    if (typeof value === "boolean") {
        return (
            <input type="checkbox" checked={value} disabled={readOnly}
                onChange={e => onChange(e.target.checked)}
                className="accent-white" />
        );
    }
    if (typeof value === "number") {
        return (
            <input type="number" value={value} disabled={readOnly}
                onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}
                className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1 font-mono focus:outline-none focus:border-white/30" />
        );
    }
    const txt = value == null ? "" : String(value);
    if (txt.length > 80) {
        return (
            <textarea value={txt} disabled={readOnly}
                onChange={e => onChange(e.target.value)}
                className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1 focus:outline-none focus:border-white/30 min-h-[60px]" />
        );
    }
    return (
        <input type="text" value={txt} disabled={readOnly}
            onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
            placeholder={value == null ? "(null)" : ""}
            className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1 focus:outline-none focus:border-white/30" />
    );
}
